import React, { useState, useRef, useEffect, FormEvent } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { marked } from "marked";
import { createUUID, translate } from "../../common";
import { useSettings } from "../../hooks";
import "./doc-agent.css";
import { ClientDocAIChatService } from "../../services";


// Re-export components
export { DocAgentOverlay } from "./doc-agent-overlay";
export { DraggableAgent as DraggableAssistant } from "./draggable-agent";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export function DocAgent() {
  const [_messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [_sessionId, setSessionId] = useState<string>(createUUID()); // Initialize with a new session ID
  const [useDocs, setUseDocs] = useState(true); // Toggle for document search
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const feedbackSectionRef = useRef<HTMLDivElement>(null);
  const [disableAutoScroll, setDisableAutoScroll] = useState(false);
  const settings = useSettings();

  

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCopyAll = () => {
    const allMessages = _messages.map((msg) => `${msg.role}: ${msg.content}`).join("\n");
    navigator.clipboard.writeText(allMessages).then(() => {
      alert("All messages copied to clipboard!");
    });
  };

  const handleClearHistory = () => {
    setInput("");
    setMessages([]);
    setSessionId(createUUID()); // Reset session ID to start a new conversation
  };

  const handleCopyLastResponse = () => {
    const lastAssistantMessage = _messages
      .slice()
      .reverse()
      .find((msg) => msg.role === "assistant");
    if (lastAssistantMessage) {
      navigator.clipboard.writeText(lastAssistantMessage.content).then(() => {
        alert("Last response copied to clipboard!");
      });
    }
  };

  const handleScroll = () => {
    const { scrollHeight, scrollTop, clientHeight } = feedbackSectionRef.current as HTMLDivElement;
    const isAtBottom = scrollHeight - scrollTop === clientHeight;
    setDisableAutoScroll(!isAtBottom);
  };

  useEffect(() => {
    feedbackSectionRef.current?.addEventListener("scroll", handleScroll);
    return () => {
      feedbackSectionRef.current?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!disableAutoScroll) {
      scrollToBottom();
    }
  }, [_messages]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    let client = new ClientDocAIChatService();

    
    setInput("");
    setIsLoading(true);

    try {
      
      let messages = [..._messages]

      messages.push({ content: input, id: createUUID(), role: "user", timestamp: new Date() });

      setMessages(messages);

      // Send chat request using agent00-sdk
      const chatResult = await client.chat(input, _sessionId);

      // Extract the response text - handle nested response structure for document search
      let responseText = chatResult.content ?? "";
      

      //responseText = responseText.replaceAll(".TXT", ""); // Remove .TXT from the response text

      // Parse markdown content and update the assistant message
      const parsedContent = marked.parse(responseText) as string;

      messages.push({ content: parsedContent, id: chatResult.messageId ?? "", role: "assistant", timestamp: new Date() });

      setMessages(messages);

      //setMessages((prev) => prev.map((msg) => (msg.id === chatResult.messageId ? { ...msg, content: parsedContent } : msg)));
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === createUUID() // Assuming the last message is the one being processed
            ? {
                ...msg,
                content: "I apologize, but I encountered an error while processing your request. Please try again.",
              }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="assistant-container">
      <div className="assistant-header mb-2">
        <h3>
          <i className="pi pi-comment mr-2 header-icon"></i>
          {translate("ASSISTANT_TITLE") || "NT Assistant"}
        </h3>
      </div>

      <div ref={feedbackSectionRef} className="message-container">
        {_messages.length === 0 && (
          <div className="empty-state text-center p-3 text-500">
            <i className="pi pi-comments text-3xl mb-2 empty-state-icon"></i>
            <p>{translate("ASSISTANT_EMPTY_STATE") || "Ask me anything to start a conversation"}</p>
          </div>
        )}
        {_messages.map((message) => (
          <div key={message.id} className={`message ${message.role === "user" ? "user-message" : "assistant-message"} mb-2`}>
            <div className="message-header flex align-items-center justify-content-between">
              <span className="font-bold">{message.role === "user" ? translate("USER") || "You" : translate("ASSISTANT") || "Assistant"}</span>
              <span className="message-time text-500">{message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
            <div className="message-content p-2">
              {message.role === "assistant" && !message.content && isLoading ? (
                <div className="thinking-animation">
                  <span className="thinking-text">Thinking</span>
                  <div className="thinking-dots">
                    <div className="thinking-dot"></div>
                    <div className="thinking-dot"></div>
                    <div className="thinking-dot"></div>
                  </div>
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: message.content }} className="markdown-content" />
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="control-panel">
        <div className="control-panel-input-section">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <InputText value={input} onChange={(e) => setInput(e.target.value)} placeholder={translate("ASSISTANT_PLACE_HOLDER") || "Type your message here..."} disabled={isLoading} className="w-full p-inputtext-sm message-input" />
            <Button className="send-button" type="submit" icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-send"} disabled={isLoading || !input.trim()} />
          </form>
        </div>

        <div className="control-panel-buttons">
          <div className="left-buttons">
            <Button className="p-button-outlined p-button-sm" onClick={handleCopyAll} icon="pi pi-copy" tooltip={translate("COPY_ALL") || "Copy all messages"} tooltipOptions={{ position: "top" }} disabled={_messages.length === 0} />
            <Button className="p-button-outlined p-button-sm" onClick={handleCopyLastResponse} icon="pi pi-file-export" tooltip="Copy last response" tooltipOptions={{ position: "top" }} disabled={_messages.filter((m) => m.role === "assistant").length === 0} />
            <Button className="p-button-outlined p-button-danger p-button-sm" onClick={handleClearHistory} icon="pi pi-trash" tooltip={translate("CLEAR_HISTORY") || "Clear history"} tooltipOptions={{ position: "top" }} disabled={_messages.length === 0} />
            <div className="checkbox-container">
              <Checkbox inputId="docs-search" checked={useDocs} onChange={(e) => setUseDocs(e.checked || false)} />
              <label htmlFor="docs-search" className="checkbox-label">
                <span className="pi pi-search"></span>
              </label>
            </div>
          </div>
          <div className="right-buttons">
            <div className="text-500 text-sm">{_messages.length > 0 && `${_messages.length} messages`}</div>
          </div>
        </div>
        <p style={{ textAlign: "center", fontSize: "10px" }}>The responses are based on a range of official sources, including legislation, regulations, instructions, circulars, and guidelines. Responses are intended for general guidance only and should be verified with the applicable official documents or authorities.</p>
      </div>
    </div>
  );
}
